import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

function csvToIds(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** URL-persisted multi-select filter state for the Jobs calendar/list page.
 * Reads and writes crew_id, salesperson_user_id, territory_id as CSV params.
 * Returns a `params` object ready to spread into `useJobs(...)`. */
export function useJobsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const crewIds = useMemo(
    () => csvToIds(searchParams.get("crew_id")),
    [searchParams],
  );
  const salespersonIds = useMemo(
    () => csvToIds(searchParams.get("salesperson_user_id")),
    [searchParams],
  );
  const territoryIds = useMemo(
    () => csvToIds(searchParams.get("territory_id")),
    [searchParams],
  );

  const setParam = useCallback(
    (key: string, ids: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (ids.length === 0) {
            next.delete(key);
          } else {
            next.set(key, ids.join(","));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setCrewIds = useCallback(
    (ids: string[]) => setParam("crew_id", ids),
    [setParam],
  );
  const setSalespersonIds = useCallback(
    (ids: string[]) => setParam("salesperson_user_id", ids),
    [setParam],
  );
  const setTerritoryIds = useCallback(
    (ids: string[]) => setParam("territory_id", ids),
    [setParam],
  );

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (crewIds.length > 0) p.crew_id = crewIds.join(",");
    if (salespersonIds.length > 0)
      p.salesperson_user_id = salespersonIds.join(",");
    if (territoryIds.length > 0) p.territory_id = territoryIds.join(",");
    return p;
  }, [crewIds, salespersonIds, territoryIds]);

  return {
    crewIds,
    salespersonIds,
    territoryIds,
    setCrewIds,
    setSalespersonIds,
    setTerritoryIds,
    params,
  };
}
